import { Injectable, Injector } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { OrderFlowTraceService } from './telemetry/order-flow-trace.service';

/**
 * Guarda do catch-all da loja: devolve o consumidor ao cardápio E registra que a rota
 * não existia.
 *
 * Por que não é um `redirectTo: ''` seco. Sem catch-all, um sufixo desconhecido lança
 * `Cannot match any routes`, que sobe ao OrderFlowErrorHandler e vira `ClientAppError`
 * — 132 a 181 devices/dia medidos em 28-31/07/2026. Um `redirectTo` puro conserta a
 * tela e **apaga a medição junto**: a navegação passa a ter sucesso, não há
 * NavigationError, e a série cai a zero. "Consertado" e "parou de ser observado" ficam
 * indistinguíveis, e a próxima rota inventada por um push ou uma campanha
 * (`/<loja>/pedido/123`) levaria milhares de clientes ao cardápio em silêncio, com o
 * painel verde.
 *
 * Emite como `ClientAppError` com `name: 'RouteNotFound'` de propósito, em vez de um
 * step novo: o `health_15m` mapeia os kinds por step explicitamente
 * (`js_error <- ClientAppError`), então um step inédito não apareceria no painel sem
 * mudar o rollup. Assim o evento entra no kind `js_error` com `dim = RouteNotFound` e
 * já é elegível ao js_error_spike e ao new_error_signature, sem tocar no monitor.
 *
 * A continuidade da série é o que prova o fix: `js_error/Error` (Cannot match any
 * routes) vai a zero e `js_error/RouteNotFound` sobe ao mesmo patamar. A soma se
 * conserva. Se RouteNotFound não aparecer perto de 132-181 devices/dia, o fix ou a
 * emissão estão errados.
 */
@Injectable({ providedIn: 'root' })
export class RouteNotFoundGuard implements CanActivate {
	/**
	 * Assinatura FIXA, nunca o caminho tentado. O new_error_signature (R6) chaveia pelo
	 * `stack_head` normalizado: pôr a URL aqui faria cada sufixo novo virar uma
	 * assinatura inédita e transformaria erro de digitação em tempestade de alerta — o
	 * defeito exato que a normalização de assinatura foi escrita para eliminar. O
	 * caminho tentado vai em `route`, que é raw e não entra na chave.
	 */
	private static readonly ASSINATURA = 'RouteNotFound';

	constructor(private router: Router, private injector: Injector) {}

	canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): UrlTree {
		const slug = this.slugDaLoja(route);

		// Fire-and-forget, como todo o resto da telemetria: nada aqui pode impedir o
		// consumidor de chegar ao cardápio.
		try {
			this.injector.get(OrderFlowTraceService).emitHealth('ClientAppError', {
				name: RouteNotFoundGuard.ASSINATURA,
				// `state.url` e não `location.pathname`: durante a navegação o pathname
				// ainda é o da tela anterior — foi por isso que os alertas de 28-29/07
				// saíram com `rota: /app/` em vez do caminho que falhou.
				route: state.url,
				message: state.url,
				message_hash: state.url,
				stack_head: RouteNotFoundGuard.ASSINATURA,
				count: 1,
			}, 'RouteNotFoundGuard.canActivate');
		} catch (e) { /* telemetria nunca derruba navegação */ }

		// Relativo à loja, não à raiz: o consumidor pediu ESTA loja e é nela que ele
		// tem de cair. Sem slug (caso que o roteador não deveria produzir), vai para a
		// raiz em vez de montar uma URL quebrada.
		return this.router.parseUrl(slug ? `/${slug}` : '/');
	}

	private slugDaLoja(route: ActivatedRouteSnapshot): string {
		try {
			// O catch-all é filho de `:restaurant`, então o slug está no pai. A subida
			// pela cadeia cobre o caso de o Angular inserir um nível intermediário.
			let atual: ActivatedRouteSnapshot = route;
			while (atual) {
				const slug = atual.params && atual.params.restaurant;
				if (slug) return slug;
				atual = atual.parent;
			}
		} catch (e) { /* cai no fallback */ }
		return '';
	}
}
